import ingrooveApi from "./api";

export const getAdminOverview = async () => {
  try {
    const response = await ingrooveApi.get("/admin/overview");
    if (response.data.isSuccess) {
      const data = response.data.data;

      // Unwrap $values from .NET JSON serializer
      if (data.recentUsers?.$values) data.recentUsers = data.recentUsers.$values;
      if (data.pendingPayoutsList?.$values) data.pendingPayoutsList = data.pendingPayoutsList.$values;

      return data;
    }
    return null;
  } catch (err) {
    console.error("Failed to fetch admin overview:", err);
    return null;
  }
};

export const getAdminUsers = async (filter = {}) => {
  try {
    const params = {
      pageNumber: filter.pageNumber ?? 1,
      pageSize: filter.pageSize ?? 20,
      ...(filter.search && { search: filter.search }),
    };
    const response = await ingrooveApi.get("/admin/users", { params });
    if (response.data.isSuccess) {
      const paged = response.data.data;
      return {
        data: paged.data?.$values ?? paged.data ?? [],
        totalRecords: paged.totalRecords,
        totalPages: paged.totalPages,
        currentPage: paged.currentPage,
      };
    }
    return { data: [], totalRecords: 0, totalPages: 0 };
  } catch (err) {
    console.error("Failed to fetch users:", err);
    return { data: [], totalRecords: 0, totalPages: 0 };
  }
};

export const getAdminUserDetail = async (userId) => {
  try {
    const response = await ingrooveApi.get(`/admin/users/${userId}`);
    if (response.data.isSuccess) {
      const data = response.data.data;
      if (data.wallet?.transactions?.$values)
        data.wallet.transactions = data.wallet.transactions.$values;
      if (data.tickets?.$values)
        data.tickets = data.tickets.$values;
      data.tickets?.forEach(t => {
        if (t.extras?.$values) t.extras = t.extras.$values;
      });
      return data;
    }
    return null;
  } catch (err) {
    console.error("Failed to fetch user detail:", err);
    return null;
  }
};

export const getAdminHubs = async (filter = {}) => {
  try {
    const params = {
      pageNumber: filter.pageNumber ?? 1,
      pageSize: filter.pageSize ?? 20,
      ...(filter.search && { search: filter.search }),
    };
    const response = await ingrooveApi.get("/admin/hubs", { params });
    if (response.data.isSuccess) {
      const paged = response.data.data;
      return {
        data: paged.data?.$values ?? paged.data ?? [],
        totalRecords: paged.totalRecords,
        totalPages: paged.totalPages,
      };
    }
    return { data: [], totalRecords: 0, totalPages: 0 };
  } catch (err) {
    console.error("Failed to fetch hubs:", err);
    return { data: [], totalRecords: 0, totalPages: 0 };
  }
};

export const getAdminHubPartners = async (filter = {}) => {
  try {
    const params = {
      pageNumber: filter.pageNumber ?? 1,
      pageSize: filter.pageSize ?? 20,
      ...(filter.search && { search: filter.search }),
      ...(filter.hubId && { hubId: filter.hubId }),
    };
    const response = await ingrooveApi.get("/admin/hub-partners", { params });
    if (response.data.isSuccess) {
      const paged = response.data.data;
      return {
        data: paged.data?.$values ?? paged.data ?? [],
        totalRecords: paged.totalRecords,
        totalPages: paged.totalPages,
      };
    }
    return { data: [], totalRecords: 0, totalPages: 0 };
  } catch (err) {
    console.error("Failed to fetch hub partners:", err);
    return { data: [], totalRecords: 0, totalPages: 0 };
  }
};

export const getAdminEvents = async (filter = {}) => {
  try {
    const params = {
      pageNumber: filter.pageNumber ?? 1,
      pageSize: filter.pageSize ?? 20,
      ...(filter.search && { search: filter.search }),
      ...(filter.hubId && { hubId: filter.hubId }),
      ...(filter.fromDate && { fromDate: filter.fromDate }),
      ...(filter.toDate && { toDate: filter.toDate }),
    };
    const response = await ingrooveApi.get("/admin/events", { params });
    if (response.data.isSuccess) {
      const paged = response.data.data;
      return {
        data: paged.data?.$values ?? paged.data ?? [],
        totalRecords: paged.totalRecords,
        totalPages: paged.totalPages,
      };
    }
    return { data: [], totalRecords: 0, totalPages: 0 };
  } catch (err) {
    console.error("Failed to fetch events:", err);
    return { data: [], totalRecords: 0, totalPages: 0 };
  }
};

export const getAdminChatRooms = async (filter = {}) => {
  try {
    const params = {
      pageNumber: filter.pageNumber ?? 1,
      pageSize: filter.pageSize ?? 20,
      ...(filter.search && { search: filter.search }),
    };
    const response = await ingrooveApi.get("/admin/chat-rooms", { params });
    if (response.data.isSuccess) {
      const paged = response.data.data;
      return {
        data: paged.data?.$values ?? paged.data ?? [],
        totalRecords: paged.totalRecords,
        totalPages: paged.totalPages,
      };
    }
    return { data: [], totalRecords: 0, totalPages: 0 };
  } catch (err) {
    console.error("Failed to fetch chat rooms:", err);
    return { data: [], totalRecords: 0, totalPages: 0 };
  }
};

export const getChatRoomMessages = async (chatRoomId) => {
  try {
    const response = await ingrooveApi.get(`/admin/chat-rooms/${chatRoomId}/messages`);
    if (response.data.isSuccess) {
      return response.data.data?.$values ?? response.data.data ?? [];
    }
    return [];
  } catch (err) {
    console.error("Failed to fetch messages:", err);
    return [];
  }
};

export const deleteChatMessage = async (messageId) => {
  try {
    const response = await ingrooveApi.delete(`/admin/chat-messages/${messageId}`);
    return response.data.isSuccess;
  } catch (err) {
    console.error("Failed to delete message:", err);
    return false;
  }
};

export const getAdminPayouts = async (filter = {}) => {
  try {
    const params = {
      pageNumber: filter.pageNumber ?? 1,
      pageSize: filter.pageSize ?? 20,
      ...(filter.search && { search: filter.search }),
      ...(filter.status && { status: filter.status }),
      ...(filter.fromDate && { fromDate: filter.fromDate }),
      ...(filter.toDate && { toDate: filter.toDate }),
    };
    const response = await ingrooveApi.get("/admin/payouts", { params });
    if (response.data.isSuccess) {
      const paged = response.data.data;
      return {
        data: paged.data?.$values ?? paged.data ?? [],
        totalRecords: paged.totalRecords,
        totalPages: paged.totalPages,
      };
    }
    return { data: [], totalRecords: 0, totalPages: 0 };
  } catch (err) {
    console.error("Failed to fetch payouts:", err);
    return { data: [], totalRecords: 0, totalPages: 0 };
  }
};

export const approvePayout = async (payoutId) => {
  try {
    const response = await ingrooveApi.post("/payout/approve", JSON.stringify(payoutId));
    return response.data;
  } catch (err) {
    console.error("Failed to approve payout:", err);
    throw err;
  }
};

export const adminRefundTicketSale = async (ticketSaleId, reason = "") => {
  try {
    const response = await ingrooveApi.post("/admin/refund", {
      ticketSaleId,
      reason,
    });
    return response.data;
  } catch (err) {
    console.error("Failed to process refund:", err);
    return { isSuccess: false, message: "Request failed." };
  }
};

export const getAdminFinancials = async (year = null) => {
  try {
    const params = year ? { year } : {};
    const response = await ingrooveApi.get("/admin/financials", { params });
    if (response.data.isSuccess) {
      const data = response.data.data;
      if (data.topHubsByRevenue?.$values)
        data.topHubsByRevenue = data.topHubsByRevenue.$values;
      if (data.monthlyRevenue?.$values)
        data.monthlyRevenue = data.monthlyRevenue.$values;
      return data;
    }
    return null;
  } catch (err) {
    console.error("Failed to fetch financials:", err);
    return null;
  }
};

export const getAdminSystemWallet = async (filter = {}) => {
  try {
    const params = {
      pageNumber: filter.pageNumber ?? 1,
      pageSize: filter.pageSize ?? 30,
      ...(filter.search && { search: filter.search }),
      ...(filter.fromDate && { fromDate: filter.fromDate }),
      ...(filter.toDate && { toDate: filter.toDate }),
    };
    const response = await ingrooveApi.get("/admin/system-wallet", { params });
    if (response.data.isSuccess) {
      const data = response.data.data;
      if (data.recentTransactions?.$values)
        data.recentTransactions = data.recentTransactions.$values;
      return data;
    }
    return null;
  } catch (err) {
    console.error("Failed to fetch system wallet:", err);
    return null;
  }
};

export const getAdminHubDetail = async (hubId) => {
  try {
    const response = await ingrooveApi.get(`/admin/hubs/${hubId}`);
    if (response.data.isSuccess) {
      const data = response.data.data;
      if (data.wallet?.transactions?.$values)
        data.wallet.transactions = data.wallet.transactions.$values;
      return data;
    }
    return null;
  } catch (err) {
    console.error("Failed to fetch hub detail:", err);
    return null;
  }
};

export const getAdminHubEvents = async (hubId) => {
  try {
    const response = await ingrooveApi.get(`/statistics/admin/hub/${hubId}/events`);
    if (response.data.isSuccess) {
      const data = response.data.data;
      return {
        chartData: data.chartData?.$values ?? data.chartData ?? [],
        events: data.events?.$values ?? data.events ?? [],
      };
    }
    return { chartData: [], events: [] };
  } catch (err) {
    console.error("Failed to fetch hub events:", err);
    return { chartData: [], events: [] };
  }
};

export const getAdminEventDetail = async (eventId) => {
  try {
    const response = await ingrooveApi.get(`/admin/events/${eventId}`);
    if (response.data.isSuccess) {
      const data = response.data.data;
      if (data.audience?.$values) data.audience = data.audience.$values;
      if (data.ticketTiers?.$values) data.ticketTiers = data.ticketTiers.$values;
      if (data.extraTiers?.$values) data.extraTiers = data.extraTiers.$values;
      if (data.dates?.$values) data.dates = data.dates.$values;
      data.audience?.forEach(a => {
        if (a.extras?.$values) a.extras = a.extras.$values;
      });
      return data;
    }
    return null;
  } catch (err) {
    console.error("Failed to fetch event detail:", err);
    return null;
  }
};
